import { Assistant } from "../../contexts/AssistantContext";
import { AssistantActiveState, AssistantStatus, AssistantTableData, AppearanceState, AssistantFormValues, OpenAIConfig, PineconeConfig, AssistantData, GuardrailConfig } from "./types";
import { DEFAULT_APPEARANCE_STATE } from './constants';
import type { ColorPickerProps } from 'antd/es/color-picker';

import { createLogger } from '../../utils/logger';
const logger = createLogger('features-assistant-management');

export const getTimeAgo = (timestamp: string | number): string => {
  if (!timestamp) return 'No update date';

  // Convert Unix timestamp (seconds) to milliseconds
  const date = new Date(typeof timestamp === 'string' ? parseInt(timestamp) * 1000 : timestamp * 1000);
  if (isNaN(date.getTime())) return 'Invalid date';

  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  let interval = seconds / 31536000; // years
  if (interval > 1) return `${Math.floor(interval)} years ago`;

  interval = seconds / 2592000; // months
  if (interval > 1) return `${Math.floor(interval)} months ago`;

  interval = seconds / 86400; // days
  if (interval > 1) return `${Math.floor(interval)} days ago`;

  interval = seconds / 3600; // hours
  if (interval > 1) return `${Math.floor(interval)} hours ago`;

  interval = seconds / 60; // minutes
  if (interval > 1) return `${Math.floor(interval)} minutes ago`;

  if (seconds < 0) return 'Invalid date';
  return 'just now';
};

export const getStatusInfo = (status: AssistantStatus | number) => {
  switch (status) {
    case 1:
    case 'Active':
      return { text: 'Active', class: 'status-active' };
    case 0:
    case 'Inactive':
      return { text: 'Inactive', class: 'status-inactive' };
    default:
      return { text: 'Draft', class: 'status-unknown' };
  }
};

export const toColorString = (color: ColorPickerProps['value']): string => {
  if (typeof color === 'string') {
    return color;
  }
  if (color && 'toHexString' in color && typeof color.toHexString === 'function') {
    return color.toHexString();
  }
  return '#000000';
};

export const formatTableData = (assistants: Assistant[]): AssistantTableData[] => assistants.map((bot) => ({
  key: bot.id,
  id: bot.id,
  name: bot.name,
  status: AssistantStatus.ACTIVE, // Valor por defecto
  activeState: AssistantActiveState.SCHEDULED, // Valor por defecto
  schedule: [
    { active: true, time: '09:00-17:00 (Mon–Fri)' },
    { active: false, time: '09:00-17:00 (Mon–Fri)' },
  ],
  lastSync: bot.createdAt ? new Date(bot.createdAt * 1000).toISOString() : new Date().toISOString(),
  createdAt: bot.createdAt ? new Date(bot.createdAt * 1000).toISOString() : new Date().toISOString(),
})).sort((a, b) => {
  // mismo status → comparar por nombre
  if (a.status === b.status) {
    return a.name.localeCompare(b.name);
  }
  // Active siempre antes que Inactive
  return a.status === 'Active' ? -1 : 1;
});

export const getAppearanceStateFromAssistant = (assistant: any): AppearanceState => {
  if (!assistant?.webchatConfig) {
    return DEFAULT_APPEARANCE_STATE;
  }

  const { webchatConfig } = assistant;

  return {
    primaryColor: webchatConfig.colorTitle || DEFAULT_APPEARANCE_STATE.primaryColor,
    backgroundColor: webchatConfig.colorBg || DEFAULT_APPEARANCE_STATE.backgroundColor,
    textColor: webchatConfig.chatbotTextMessageColor || DEFAULT_APPEARANCE_STATE.textColor,
    buttonColor: webchatConfig.iconBgColor || DEFAULT_APPEARANCE_STATE.buttonColor,
    backgroundMessageColor: webchatConfig.chatbotBgMessageColor || DEFAULT_APPEARANCE_STATE.backgroundMessageColor,
    colorTitleText: webchatConfig.colorMain || DEFAULT_APPEARANCE_STATE.colorTitleText,
    iconColor: webchatConfig.iconBgColor || DEFAULT_APPEARANCE_STATE.iconColor,
    greetingMessage: webchatConfig.greeting || DEFAULT_APPEARANCE_STATE.greetingMessage,
  };
};

export const getIntegrationCode = (domain: string, assistantId?: string): string => {
  return `<script src="https://${domain}/lib/init.js" data-chatbot-id="${assistantId}"></script>`;
};

export const handleCopy = (code: string): void => {
  navigator.clipboard.writeText(code);
};

const getMaxTokensByLength = (length: string): number => {
  switch (length) {
    case 'Concise':
      return 300;
    case 'Standard':
      return 450;
    case 'Extended':
      return 600;
    default:
      return 450;
  }
};

export const getResponseLength = (maxTokens?: number): string => {
  if (!maxTokens) return '';
  if (maxTokens === 300) return 'Concise';
  if (maxTokens === 450) return 'Standard';
  if (maxTokens >= 600) return 'Extended';
  return '';
};

const removeAccents = (str: string): string => {
  const normalized = str.normalize('NFD');
  const result = normalized.replace(/[\u0300-\u036f]/g, '');
  return result;
};

/**
 * Convert GuardrailConfig[] to backend format (inputGuards/outputGuards)
 */
const convertGuardrailsToBackendFormat = (guardrails: GuardrailConfig[]) => {
  const enabledGuards = guardrails.filter(guard => guard.enabled);

  // Separate guards by whether they need LLM processing
  // Fast guards (pattern-based) go to inputGuards
  // LLM guards go to outputGuards
  const inputGuards: string[] = [];
  const outputGuards: string[] = [];

  enabledGuards.forEach(guard => {
    if (guard.requiresLLM) {
      outputGuards.push(guard.id);
    } else {
      inputGuards.push(guard.id);
    }
  });

  return { inputGuards, outputGuards };
};

export const transformAssistantData = (values: AssistantFormValues, currentAssistant: AssistantData | null = null) => {
  // Convert guardrails from UI format to backend format
  const guardrailsConfig = values.advancedConfig?.guardrails
    ? convertGuardrailsToBackendFormat(values.advancedConfig.guardrails)
    : { inputGuards: [], outputGuards: [] };

  logger.info('Converting guardrails to backend format:', {
    original: values.advancedConfig?.guardrails,
    converted: guardrailsConfig
  });

  return {
    name: values.agentName,
    businessName: values.businessName,
    communicationStyle: values.communicationStyle,
    domainTopic: values.domainTopics,
    chatTopics: values.chatTopics,
    specialist: values.role,
    fallbackResponse: values.fallbackResponse,
    voiceLanguage: values.voiceLanguage,
    webchatConfig: {
      ...currentAssistant?.webchatConfig,
      voiceLanguage: values.voiceLanguage,
      // Set default colors for new agents
      colorTitle: currentAssistant?.webchatConfig?.colorTitle || DEFAULT_APPEARANCE_STATE.primaryColor,
      colorMain: currentAssistant?.webchatConfig?.colorMain || DEFAULT_APPEARANCE_STATE.colorTitleText,
      iconBgColor: currentAssistant?.webchatConfig?.iconBgColor || DEFAULT_APPEARANCE_STATE.iconColor,
      greeting: currentAssistant?.webchatConfig?.greeting || DEFAULT_APPEARANCE_STATE.greetingMessage,
    },
    // Include guardrails configuration in the proper backend format
    guardrailsConfig,
    // Include advanced configuration (for potential future use)
    advancedConfig: {
      guardrails: values.advancedConfig?.guardrails || [],
      businessRules: values.advancedConfig?.businessRules || [],
      acronyms: values.advancedConfig?.acronyms || [],
    },
    // Include business rules separately for backward compatibility
    businessRules: values.advancedConfig?.businessRules?.map((rule: any) =>
      typeof rule === 'string' ? rule : rule.rule
    ) || [],
    openAIConfig: {
      max_tokens:
        currentAssistant?.openAIConfig?.max_tokens || getMaxTokensByLength(values.responseLength),
      // Use model from form selection, fallback to existing config, then default
      model: values.selectedModel || currentAssistant?.openAIConfig?.model || 'gpt-4o',
      // Only send api_key if user explicitly entered a new one
      // When empty, backend preserves the existing key in DynamoDB
      ...(values.modelApiKey ? { api_key: values.modelApiKey } : {}),
      frequency_penalty: currentAssistant?.openAIConfig?.frequency_penalty || 0,
      presence_penalty: currentAssistant?.openAIConfig?.presence_penalty || 0,
      streaming: currentAssistant?.openAIConfig?.streaming || true,
      temperature: currentAssistant?.openAIConfig?.temperature || 1,
      top_p: currentAssistant?.openAIConfig?.top_p || 1,
    },
    pineconeConfig: {
      // API credentials are now managed by backend - only send if user explicitly provided them
      ...(currentAssistant?.pineconeConfig?.apiKey && { apiKey: currentAssistant.pineconeConfig.apiKey }),
      indexName: currentAssistant?.pineconeConfig?.indexName,
      // Namespace will be set by backend as ${STAGE}-${chatbotId} when creating new assistant
      // For updates, keep existing namespace
      ...(currentAssistant?.pineconeConfig?.namespace && { namespace: currentAssistant.pineconeConfig.namespace }),
      ...(currentAssistant?.pineconeConfig?.projectId && { projectId: currentAssistant.pineconeConfig.projectId }),
      ...(currentAssistant?.pineconeConfig?.environment && { environment: currentAssistant.pineconeConfig.environment }),
    },
    responseLength: values.responseLength,
    ...(values.whatsappMode ? { whatsappMode: values.whatsappMode } : {}),
  };
};

export const transformAIResponseData = (aiResponse: string) => {
  const parsedData = JSON.parse(aiResponse);

  const communicationStyleMap: Record<string, string> = {
    professional: 'professional',
    neutral: 'neutral',
    'matter-of-fact': 'matter-of-fact',
    'matter of fact': 'matter-of-fact',
    friendly: 'friendly',
    humorous: 'humorous',
  };

  return {
    agentName: parsedData.agent_name,
    businessName: parsedData.business_name,
    communicationStyle:
      communicationStyleMap[parsedData.communication_style.toLowerCase()] || 'neutral',
    responseLength: parsedData.answers_length,
    domainTopics: parsedData.domain_topics.join(' '),
    role: parsedData.role,
    fallbackResponse: parsedData.fallback_responses.join('. '),
    voiceLanguage: 'off',
  };
};